import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';

import { PartnerAcknowledgement, PartnerAuthentication } from '../../models';
import { AnalysisState } from './analysis.state';
import { ApbModalState, ApbState } from './apb-state';
import * as applicationState from './application-state.actions';
import { CmaState } from './cma-state';
import { FundsToExploreState } from './funds-to-explore.state';
import { GenerateReportDataState } from './generate-report-data.state';
import { ImportPartnerPortfoliosState } from './import-partner-portfolios.state';
import { IretireUserDataState } from './iretire-user-data.state';
import { MEState } from './me-state';
import { PEState } from './pe-state';
import { PerformanceState } from './performance.state';
import { PortfolioIdsState } from './portfolio-ids.state';
import { ProposalState } from './proposal-state';
import { ResumeExcelDownloadState } from './resume-excel-download.state';
import { RiskState } from './risk.state';
import { SelectedPortfolioIndexState } from './selected-portfolio-index.state';

export interface ApplicationState {
	peState: PEState;
	apbState: ApbState;
	proposalState: ProposalState;
	performanceState: PerformanceState;
	riskState: RiskState;
	fundsToExploreState: FundsToExploreState;
	portfolioIdsState: PortfolioIdsState;
	selectedPortfolioIndexState: SelectedPortfolioIndexState;
	resumeExcelDownloadState: ResumeExcelDownloadState;
	generateReportDataState: GenerateReportDataState;
	analysisState: AnalysisState;
	featureFlags: string[];
	cmaState: CmaState;
	iretireUserData: IretireUserDataState;
	hasAllPortfoliosTaxQualified: boolean;
	meState: MEState;
	taxPortfolioAnalysisType: string;
	isDismissedFundsUpdated: boolean;
	importPartnerPortfoliosState: ImportPartnerPortfoliosState;
	partnerAuthenticationState: PartnerAuthentication;
	partnerAcknowledgementState: PartnerAcknowledgement;
}

export const initialApplicationState: ApplicationState = {
	peState: new PEState(),
	apbState: new ApbState(),
	proposalState: new ProposalState(),
	performanceState: new PerformanceState(),
	riskState: new RiskState(),
	fundsToExploreState: new FundsToExploreState(),
	portfolioIdsState: new PortfolioIdsState(),
	selectedPortfolioIndexState: new SelectedPortfolioIndexState(),
	resumeExcelDownloadState: new ResumeExcelDownloadState(),
	generateReportDataState: new GenerateReportDataState(),
	analysisState: new AnalysisState(),
	featureFlags: [],
	cmaState: new CmaState(),
	iretireUserData: {},
	hasAllPortfoliosTaxQualified: false,
	meState: new MEState(),
	taxPortfolioAnalysisType: 'none',
	isDismissedFundsUpdated: false,
	importPartnerPortfoliosState: new ImportPartnerPortfoliosState(),
	partnerAuthenticationState: {},
	partnerAcknowledgementState: {}
};

export function applicationStateReducer(state = initialApplicationState, action: applicationState.Actions): ApplicationState {
	const apbAdapter: EntityAdapter<ApbModalState> = createEntityAdapter<ApbModalState>();
	switch (action.type) {
		case applicationState.UPDATE_PE_MODEL_VISIBLE: {
			return {
				...state,
				peState: {
					...state.peState,
					isModalVisible: action.payload
				}
			};
		}
		case applicationState.UPDATE_PE_MODEL_UNIQUE_ID: {
			return {
				...state,
				peState: {
					...state.peState,
					uniqueIdForPortfolioEditor: action.payload
				}
			};
		}
		case applicationState.UPDATE_PE_MODEL_COMPARISON_IDS: {
			return {
				...state,
				peState: {
					...state.peState,
					comparisonIds: action.payload
				}
			};
		}
		case applicationState.UPDATE_PE_MODEL_ENGAGEMENT_ID: {
			return {
				...state,
				peState: {
					...state.peState,
					engagementId: action.payload
				}
			};
		}
		case applicationState.CLEAR_PE_MODEL_ENGAGEMENT_ID: {
			return {
				...state,
				peState: {
					...state.peState,
					engagementId: null
				}
			};
		}
		case applicationState.CLEAR_PE_MODEL_COMPARISON_IDS: {
			return {
				...state,
				peState: {
					...state.peState,
					comparisonIds: []
				}
			};
		}
		case applicationState.UPDATE_PE_MODEL_ASSET_CLASSES: {
			return {
				...state,
				peState: {
					...state.peState,
					assetClasses: action.payload
				}
			};
		}
		case applicationState.UPDATE_PE_MODEL_STRESS_TESTS: {
			return {
				...state,
				peState: {
					...state.peState,
					stressTests: action.payload
				}
			};
		}
		case applicationState.UPDATE_PE_MODEL_AC_BENCHMARK: {
			return {
				...state,
				peState: {
					...state.peState,
					acBenchmark: action.payload
				}
			};
		}
		case applicationState.UPDATE_PE_MODEL_ASSET_TYPES: {
			return {
				...state,
				peState: {
					...state.peState,
					assetTypes: action.payload
				}
			};
		}
		case applicationState.UPDATE_PE_MODEL_URL_IDS: {
			return {
				...state,
				peState: {
					...state.peState,
					urlIds: action.payload
				}
			};
		}
		case applicationState.UPDATE_PE_MODEL_PE_MODE: {
			return {
				...state,
				peState: {
					...state.peState,
					portfolioEditorMode: action.payload
				}
			};
		}
		case applicationState.UPDATE_APB_MODAL_DISMISSAL: {
			return {
				...state,
				apbState: {
					...state.apbState,
					warningModal: apbAdapter.upsertMany(action.payload, { ...state.apbState.warningModal })
				}
			};
		}
		case applicationState.UPDATE_PROPOSAL_PORTFOLIO_NAME: {
			return {
				...state,
				proposalState: {
					...state.proposalState,
					proposalPortfolioName: action.payload
				}
			};
		}
		case applicationState.UPDATE_PROPOSAL_METRICS: {
			return {
				...state,
				proposalState: {
					...state.proposalState,
					metrics: action.payload.metrics
				}
			};
		}
		case applicationState.UPDATE_PROPOSAL_PURPOSE: {
			return {
				...state,
				proposalState: {
					...state.proposalState,
					purpose: action.payload.purpose
				}
			};
		}
		case applicationState.UPDATE_HYPOTHETICAL_GROWTH_MULTIPLIER: {
			return {
				...state,
				performanceState: {
					...state.performanceState,
					hypotheticalGrowthMultiplier: action.payload.hypotheticalGrowthMultiplier
				}
			};
		}
		case applicationState.UPDATE_HYPOTHETICAL_GROWTH_START: {
			return {
				...state,
				performanceState: {
					...state.performanceState,
					hypotheticalGrowthStartTimestamp: action.payload.hypotheticalGrowthStartTimestamp
				}
			};
		}
		case applicationState.UPDATE_CUSTOM_ADVISORY_FEE: {
			return {
				...state,
				performanceState: {
					...state.performanceState,
					customAdvisoryFee: action.payload.customAdvisoryFee,
					includeFee: action.payload.includeFee
				}
			};
		}
		case applicationState.UPDATE_SELECTED_BENCHMARK_FOR_CAPTURE_RATIO: {
			return {
				...state,
				performanceState: {
					...state.performanceState,
					selectedBenchmarkForCaptureRatio: action.payload.selectedBenchmarkForCaptureRatio
				}
			};
		}
		case applicationState.UPDATE_KEY_RATE_SELECTED_CURRENCY: {
			return {
				...state,
				riskState: {
					...state.riskState,
					keyRateSelectedCurrency: action.payload.keyRateSelectedCurrency
				}
			};
		}
		case applicationState.UPDATE_FUNDS_TO_EXPLORE: {
			return {
				...state,
				fundsToExploreState: {
					...state.fundsToExploreState,
					fundsToExplore: action.payload
				}
			};
		}
		case applicationState.UPDATE_PORTFOLIO_IDS: {
			return {
				...state,
				portfolioIdsState: {
					...state.portfolioIdsState,
					portfolioIds: action.payload
				}
			};
		}
		case applicationState.UPDATE_SELECTED_PORTFOLIO_INDEX: {
			return {
				...state,
				selectedPortfolioIndexState: {
					...state.selectedPortfolioIndexState,
					selectedPortfolioIndex: action.payload
				}
			};
		}
		case applicationState.UPDATE_RESUME_EXCEL_DOWNLOAD: {
			return {
				...state,
				resumeExcelDownloadState: {
					...state.resumeExcelDownloadState,
					shouldResumeExcelDownload: action.payload
				}
			};
		}
		case applicationState.UPDATE_GENERATE_REPORT_DATA: {
			return {
				...state,
				generateReportDataState: action.payload
			};
		}
		case applicationState.UPDATE_FEATURE_FLAGS: {
			return {
				...state,
				featureFlags: action.payload
			};
		}
		case applicationState.UPDATE_ANALYSIS_STATE: {
			return {
				...state,
				analysisState: {
					...state.analysisState,
					...action.payload
				}
			};
		}
		case applicationState.UPDATE_CMA_IS_TRANSIENT_PORTFOLIO_EDITED: {
			return {
				...state,
				cmaState: {
					...state.cmaState,
					isTransientPortfolioEdited: action.payload
				}
			};
		}
		case applicationState.UPDATE_IRETIRE_USER_DATA_STATE: {
			return {
				...state,
				iretireUserData: {
					...state.iretireUserData,
					...action.payload
				}
			};
		}
		case applicationState.CLEAR_IRETIRE_USER_DATA: {
			return {
				...state,
				iretireUserData: {}
			};
		}
		case applicationState.UPDATE_IQ_STATUS_OR_AREA_OF_FOCUS: {
			return {
				...state,
				analysisState: {
					...state.analysisState,
					iqStatusOrAreaOfFocus: action.payload
				}
			};
		}
		case applicationState.UPDATE_PORTFOLIOS_TAX_QUALIFIED_TYPE: {
			return {
				...state,
				taxPortfolioAnalysisType: action.payload
			};
		}
		case applicationState.UPDATE_ME_SELECTED_MODELS_DATA: {
			return {
				...state,
				meState: {
					...state.meState,
					modalData: action.payload
				}
			};
		}
		case applicationState.UPDATE_IS_DISMISSED_FUNDS_UPDATED: {
			return {
				...state,
				isDismissedFundsUpdated: action.payload
			};
		}
		case applicationState.UPDATE_IMPORT_PARTNER_PORTFOLIOS_STATE: {
			return {
				...state,
				importPartnerPortfoliosState: { ...state.importPartnerPortfoliosState, ...action.payload }
			};
		}
		case applicationState.UPDATE_IMPORT_PARTNER_PORTFOLIOS_PARTNER_PLATFORM: {
			return {
				...state,
				importPartnerPortfoliosState: {
					...state.importPartnerPortfoliosState,
					partnerPlatform: action.payload,
					selectedPortfolios: new Map()
				}
			};
		}
		case applicationState.UPDATE_IMPORT_PARTNER_PORTFOLIOS_SELECTED_PORTFOLIOS: {
			return {
				...state,
				importPartnerPortfoliosState: { ...state.importPartnerPortfoliosState, selectedPortfolios: action.payload }
			};
		}
		case applicationState.UPDATE_IMPORT_PARTNER_PORTFOLIOS_IS_REFRESH_NEEDED: {
			return {
				...state,
				importPartnerPortfoliosState: { ...state.importPartnerPortfoliosState, isRefreshNeeded: action.payload }
			};
		}
		case applicationState.UPDATE_IMPORT_PARTNER_PORTFOLIOS_RESOLVED_ASSETS: {
			return {
				...state,
				importPartnerPortfoliosState: { ...state.importPartnerPortfoliosState, resolvedAssets: action.payload }
			};
		}
		case applicationState.UPDATE_IMPORT_PARTNER_PORTFOLIOS_PORTFOLIO_DATA: {
			return {
				...state,
				importPartnerPortfoliosState: { ...state.importPartnerPortfoliosState, portfolioData: action.payload }
			};
		}
		case applicationState.UPDATE_IMPORT_PARTNER_PORTFOLIOS_ACCOUNT_ERROR_MAP: {
			return {
				...state,
				importPartnerPortfoliosState: { ...state.importPartnerPortfoliosState, accountErrorMap: action.payload }
			};
		}
		case applicationState.UPDATE_PARTNER_AUTHENTICATION_INFO: {
			return {
				...state,
				partnerAuthenticationState: { ...state.partnerAuthenticationState, [action.payload.partnerId]: action.payload.partnerAuthenticationInfo }
			};
		}
		case applicationState.UPDATE_PARTNER_ACKNOWLEDGEMENT_STATE: {
			return {
				...state,
				partnerAcknowledgementState: { ...state.partnerAcknowledgementState, ...action.payload }
			};
		}
		default: {
			return state;
		}
	}
}
